// Workspace Status Indicator Component
// Architecture: v4.0.0 Multiple Charts Implementation
// Shows auto-save status and workspace information

import React, { useCallback } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Tooltip,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Switch,
  FormControl,
  FormLabel,
  Divider,
  Button
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
  SettingsIcon,
  TimeIcon
} from '@chakra-ui/icons';
import { useWorkspacePersistence } from './WorkspacePersistence';
import { useMultipleCharts } from './MultipleChartsContext';

/**
 * Workspace Status Indicator Props
 */
export interface WorkspaceStatusIndicatorProps {
  position?: 'top-right' | 'bottom-right' | 'bottom-left';
  showInDevelopment?: boolean;
  compact?: boolean;
}

/**
 * Format time ago string
 */
function formatTimeAgo(date: Date | null): string {
  if (!date) return 'Never';
  
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  
  return date.toLocaleDateString();
}

/**
 * Auto-save Status Badge
 */
interface AutoSaveStatusProps {
  isAutoSaving: boolean;
  pendingSave: boolean;
  lastSaveTime: Date | null;
  enabled: boolean;
}

const AutoSaveStatus: React.FC<AutoSaveStatusProps> = ({
  isAutoSaving,
  pendingSave,
  lastSaveTime,
  enabled
}) => {
  if (!enabled) {
    return (
      <Badge colorScheme="gray" size="sm">
        <HStack spacing={1}>
          <WarningIcon boxSize={2} />
          <Text fontSize="xs">Auto-save Off</Text>
        </HStack>
      </Badge>
    );
  }
  
  if (isAutoSaving) {
    return (
      <Badge colorScheme="blue" size="sm">
        <HStack spacing={1}>
          <Box
            w={2}
            h={2}
            borderRadius="50%"
            bg="blue.400"
            animation="pulse 1s infinite"
          />
          <Text fontSize="xs">Saving...</Text>
        </HStack>
      </Badge>
    );
  }
  
  if (pendingSave) {
    return (
      <Badge colorScheme="yellow" size="sm">
        <HStack spacing={1}>
          <TimeIcon boxSize={2} />
          <Text fontSize="xs">Pending</Text>
        </HStack>
      </Badge>
    );
  }
  
  return (
    <Badge colorScheme="green" size="sm">
      <HStack spacing={1}>
        <CheckCircleIcon boxSize={2} />
        <Text fontSize="xs">Saved</Text>
      </HStack>
    </Badge>
  );
};

/**
 * Workspace Status Settings
 */
interface WorkspaceSettingsProps {
  autoSaveConfig: any;
  updateConfig: (config: any) => void;
  onManualSave: () => void;
  onRestoreSession: () => void;
}

const WorkspaceSettings: React.FC<WorkspaceSettingsProps> = ({
  autoSaveConfig,
  updateConfig,
  onManualSave,
  onRestoreSession
}) => {
  const handleToggleAutoSave = useCallback((enabled: boolean) => {
    updateConfig({
      ...autoSaveConfig,
      enabled
    });
  }, [autoSaveConfig, updateConfig]);
  
  const handleToggleSaveOnActions = useCallback((key: string, enabled: boolean) => {
    updateConfig({
      ...autoSaveConfig,
      [key]: enabled
    });
  }, [autoSaveConfig, updateConfig]);
  
  return (
    <VStack align="stretch" spacing={3}>
      {/* Auto-save Toggle */}
      <FormControl display="flex" alignItems="center">
        <FormLabel htmlFor="auto-save" mb="0" fontSize="sm">
          Auto-save enabled
        </FormLabel>
        <Switch
          id="auto-save"
          isChecked={autoSaveConfig.enabled}
          onChange={(e) => handleToggleAutoSave(e.target.checked)}
          size="sm"
        />
      </FormControl>
      
      <Divider />
      
      {/* Save Triggers */}
      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
        Save triggers:
      </Text>
      
      <VStack align="stretch" spacing={2}>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="save-tab-switch" mb="0" fontSize="xs">
            On tab switch
          </FormLabel>
          <Switch
            id="save-tab-switch"
            isChecked={autoSaveConfig.saveOnTabSwitch}
            onChange={(e) => handleToggleSaveOnActions('saveOnTabSwitch', e.target.checked)}
            size="sm"
            isDisabled={!autoSaveConfig.enabled}
          />
        </FormControl>
        
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="save-chart-load" mb="0" fontSize="xs">
            On chart load
          </FormLabel>
          <Switch
            id="save-chart-load"
            isChecked={autoSaveConfig.saveOnChartLoad}
            onChange={(e) => handleToggleSaveOnActions('saveOnChartLoad', e.target.checked)}
            size="sm"
            isDisabled={!autoSaveConfig.enabled}
          />
        </FormControl>
        
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="save-chart-close" mb="0" fontSize="xs">
            On chart close
          </FormLabel>
          <Switch
            id="save-chart-close"
            isChecked={autoSaveConfig.saveOnChartClose}
            onChange={(e) => handleToggleSaveOnActions('saveOnChartClose', e.target.checked)}
            size="sm"
            isDisabled={!autoSaveConfig.enabled}
          />
        </FormControl>
      </VStack>
      
      <Divider />
      
      {/* Manual Actions */}
      <VStack align="stretch" spacing={2}>
        <Button size="sm" onClick={onManualSave} colorScheme="blue">
          Save Now
        </Button>
        
        <Button size="sm" onClick={onRestoreSession} variant="outline">
          Restore Session
        </Button>
      </VStack>
    </VStack>
  );
};

/**
 * Workspace Status Indicator Component
 */
export const WorkspaceStatusIndicator: React.FC<WorkspaceStatusIndicatorProps> = ({
  position = 'bottom-right',
  showInDevelopment = true,
  compact = false
}) => {
  const { workspace, charts, loadedChartIds } = useMultipleCharts();
  const {
    autoSaveConfig,
    lastSaveTime,
    isAutoSaving,
    pendingSave,
    saveToSession,
    restoreFromSession,
    updateConfig
  } = useWorkspacePersistence();
  
  // Only show in development by default, or if explicitly enabled
  if (process.env.NODE_ENV === 'production' && showInDevelopment) {
    return null;
  }
  
  const positionStyles = {
    'top-right': { top: 4, right: 4 },
    'bottom-right': { bottom: 4, right: 4 },
    'bottom-left': { bottom: 4, left: 4 }
  };
  
  if (compact) {
    return (
      <Box
        position="fixed"
        {...positionStyles[position]}
        zIndex={1000}
        bg="white"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
        p={2}
      >
        <Tooltip
          label={`Auto-save: ${autoSaveConfig.enabled ? 'ON' : 'OFF'} • Last saved: ${formatTimeAgo(lastSaveTime)}`}
          hasArrow
        >
          <Box>
            <AutoSaveStatus
              isAutoSaving={isAutoSaving}
              pendingSave={pendingSave}
              lastSaveTime={lastSaveTime}
              enabled={autoSaveConfig.enabled}
            />
          </Box>
        </Tooltip>
      </Box>
    );
  }
  
  return (
    <Box
      position="fixed"
      {...positionStyles[position]}
      zIndex={1000}
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="md"
      p={3}
      minW="200px"
      opacity={0.9}
      _hover={{ opacity: 1 }}
    >
      {/* Header */}
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Workspace
        </Text>
        
        <Popover placement="left-start">
          <PopoverTrigger>
            <IconButton
              aria-label="Workspace settings"
              icon={<SettingsIcon />}
              size="xs"
              variant="ghost"
            />
          </PopoverTrigger>
          <PopoverContent w="280px">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>
              <Text fontSize="sm" fontWeight="semibold">
                Auto-save Settings
              </Text>
            </PopoverHeader>
            <PopoverBody>
              <WorkspaceSettings
                autoSaveConfig={autoSaveConfig}
                updateConfig={updateConfig}
                onManualSave={saveToSession}
                onRestoreSession={restoreFromSession}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
      
      {/* Status */}
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.600">Status:</Text>
          <AutoSaveStatus
            isAutoSaving={isAutoSaving}
            pendingSave={pendingSave}
            lastSaveTime={lastSaveTime}
            enabled={autoSaveConfig.enabled}
          />
        </HStack>
        
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.600">Charts:</Text>
          <Text fontSize="xs" color="gray.800">
            {loadedChartIds.length} / {workspace.maxOpenCharts}
          </Text>
        </HStack>
        
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.600">Last saved:</Text>
          <Text fontSize="xs" color="gray.800">
            {formatTimeAgo(lastSaveTime)}
          </Text>
        </HStack>
        
        {autoSaveConfig.enabled && (
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.600">Interval:</Text>
            <Text fontSize="xs" color="gray.800">
              {Math.round(autoSaveConfig.intervalMs / 1000)}s
            </Text>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default WorkspaceStatusIndicator;